import {
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import {
  Cached,
  CreateNewFolder,
  Delete,
  GetApp,
  Publish,
} from "@material-ui/icons";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { fileType } from "../../../common/AppConstant";
import { selector as authStoreSelector } from "../../../store/AuthStore";
import {
  fetchChangeSelectedFileInfos,
  fetchCreateDownloadZipFilePath,
  fetchDeleteFile,
  fetchDownloadFile,
  fetchListFiles,
  fetchSortFileInfos,
  selector as fileStoreSelector,
} from "../../../store/FileStore";
import {
  fetchListOfficeInfos,
  selector as officeStoreSelector,
} from "../../../store/OfficeStore";
import { ProgressView } from "../../share";
import useStyles from "../useStyles";

import CreateFolderDialog from "./CreateFolderDialog";
import FileActionDialog from "./FileActionDialog";
import FilesTableRow from "./FilesTableRow";
import FolderBreadcrumbList from "./FolderBreadcrumbList";
import UploadDialog from "./UploadDialog";

const iconColor = "inherit";
const iconSize = "large";
const iconEdge = "end";

/**
 * FilesCardコンポーネント.
 *
 * @return {object} コンポーネント
 */
function FilesCard() {
  // スタイル
  const styles = useStyles();

  // Dispatch
  const dispatch = useDispatch();

  // ダウンロードダイアログの表示状態State（true: 表示、false: 非表示）
  const [isOpenDownloadDialog, setIsOpenDownloadDialog] = React.useState(false);

  // 削除ダイアログの表示状態State（true: 表示、false: 非表示）
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = React.useState(false);

  // アップロードダイアログの表示状態State（true: 表示、false: 非表示）
  const [isOpenUploadDialog, setIsOpenUploadDialog] = React.useState(false);

  // フォルダ作成ダイアログの表示状態State（true: 表示、false: 非表示）
  const [
    isOpenCreateFolderDialog,
    setIsOpenCreateFolderDialog,
  ] = React.useState(false);

  const [sortState, setSortState] = React.useState({
    order: "asc", // 降順("desc"), 昇順("asc")
    key: "fileName", // ソート中の列
  });

  const {
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading,
    // ファイル情報一覧
    fileInfos,
    // ファイルプレフィックス
    filePrefix,
    // 選択済みファイル情報一覧
    selectedFileInfos,
  } = useSelector(fileStoreSelector);

  const {
    // ダウンロード可能フラグ（true: 可能、false: 不可能）
    isDownloadable,
    // アップロード可能フラグ（true: 可能、false: 不可能）
    isUploadable,
  } = useSelector(authStoreSelector);

  const {
    // 選択されている拠点情報
    selectedOfficeInfo,
  } = useSelector(officeStoreSelector);

  // 副作用
  React.useEffect(() => {
    dispatch(fetchListOfficeInfos());
  }, [dispatch]);

  // 副作用
  React.useEffect(() => {
    if (filePrefix) {
      dispatch(fetchListFiles(filePrefix));
      setSortState({
        order: "asc",
        key: "fileName",
      });
    }
  }, [dispatch, filePrefix]);

  // ファイル情報一覧の中でファイル種別がFILEとなっているファイル情報一覧
  const fileTypeFileInfos = React.useMemo(
    () => fileInfos.filter((fileInfo) => fileInfo.fileType === fileType.file),
    [fileInfos]
  );

  // チェックボックス変更時の処理
  const handleCheckboxChange = (event) => {
    if (event && event.target) {
      if (
        filePrefix.startsWith(`${selectedOfficeInfo.serialNumber}/DOWNLOAD/`)
      ) {
        // DOWNLOADフォルダ配下では、ファイルとフォルダの全てを選択済みファイル情報一覧に追加する
        dispatch(
          fetchChangeSelectedFileInfos({
            fileInfos,
            checked: event.target.checked,
          })
        );
      } else {
        // DOWNLOADフォルダ配下以外では、ファイルのみを選択済みファイル情報一覧に追加する
        dispatch(
          fetchChangeSelectedFileInfos({
            fileInfos: fileTypeFileInfos,
            checked: event.target.checked,
          })
        );
      }
    }
  };

  const handleClickSortColumn = (column) => {
    const isDesc = column === sortState.key && sortState.order === "desc";
    const nextOrder = isDesc ? "asc" : "desc";
    const payload = { column, nextOrder };
    setSortState({
      order: nextOrder,
      key: column,
    });
    dispatch(fetchSortFileInfos(payload));
  };

  const onActionDownloadFile = () => {
    // selectedFileInfosからfileTypeがfolderのリストを作成
    const folderFileInfos = selectedFileInfos.filter(
      (fileInfo) => fileInfo.fileType === fileType.folder
    );
    // selectedFileInfosからfileTypeがfileのリストを作成
    const fileFileInfos = selectedFileInfos.filter(
      (fileInfo) => fileInfo.fileType === fileType.file
    );

    //  フォルダを選択していた場合
    if (folderFileInfos) {
      // フォルダ内の全ファイルをダウンロードする
      dispatch(
        fetchCreateDownloadZipFilePath(
          folderFileInfos,
          selectedOfficeInfo.label
        )
      );
    }
    // ファイルを選択していた場合
    if (fileFileInfos) {
      dispatch(fetchDownloadFile(fileFileInfos));
    }
  };

  const downloadMessage = `選択済みのファイル、もしくはフォルダをダウンロードしますか？
※フォルダを選択した場合、サイズは表示されません。`;

  return (
    <>
      {/* ファイルテーブルカード */}
      <Card className={styles.card}>
        <CardContent className={`${styles.cardContent} ${styles.flexStart}`}>
          <FolderBreadcrumbList />
          <div className={styles.marginLeftAuto}>
            <IconButton
              color={iconColor}
              edge={iconEdge}
              disabled={!filePrefix || !selectedFileInfos.length || isLoading}
              style={{
                display: isDownloadable ? "" : "none",
              }}
              onClick={() => setIsOpenDownloadDialog(true)}
            >
              <GetApp fontSize={iconSize} />
            </IconButton>
            <IconButton
              color={iconColor}
              edge={iconEdge}
              // フォルダが選択された場合は、削除アクションボタンを無効化にする
              disabled={
                !filePrefix ||
                isLoading ||
                !selectedFileInfos.length ||
                selectedFileInfos
                  .map((fileInfo) => fileInfo.fileType)
                  .includes(fileType.folder)
              }
              style={{
                display: isUploadable ? "" : "none",
              }}
              onClick={() => setIsOpenDeleteDialog(true)}
            >
              <Delete fontSize={iconSize} />
            </IconButton>
            {/* DOWNLOADフォルダ配下ではフォルダ作成アクションボタンは非表示にする */}
            {!filePrefix.startsWith(
              `${selectedOfficeInfo.serialNumber}/DOWNLOAD/`
            ) && (
              <IconButton
                color={iconColor}
                edge={iconEdge}
                disabled={!filePrefix || isLoading}
                style={{
                  display: isUploadable ? "" : "none",
                }}
                onClick={() => setIsOpenCreateFolderDialog(true)}
              >
                <CreateNewFolder fontSize={iconSize} />
              </IconButton>
            )}
            <IconButton
              color={iconColor}
              edge={iconEdge}
              disabled={!filePrefix || isLoading}
              style={{
                display: isUploadable ? "" : "none",
              }}
              onClick={() => setIsOpenUploadDialog(true)}
            >
              <Publish fontSize={iconSize} />
            </IconButton>
            <IconButton
              color={iconColor}
              edge={iconEdge}
              disabled={!filePrefix || isLoading}
              onClick={() => {
                dispatch(fetchListOfficeInfos());
                dispatch(fetchListFiles(filePrefix));
              }}
            >
              <Cached fontSize={iconSize} />
            </IconButton>
          </div>
        </CardContent>
        <CardContent className={styles.cardContent}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    // 以下のいずれかの条件に当てはまる場合はチェックボックスを無効化にする
                    // 1. filePrefixが未設定の場合
                    // 2. ファイルが0件の場合、かつ、filePrefixが「selectedOfficeInfo.serialNumber/DOWNLOAD/」で始まっていない場合
                    // 3. ローディング中の場合
                    disabled={
                      !filePrefix ||
                      (!fileTypeFileInfos.length &&
                        !filePrefix.startsWith(
                          `${selectedOfficeInfo.serialNumber}/DOWNLOAD/`
                        )) ||
                      isLoading
                    }
                    indeterminate={
                      selectedFileInfos.length > 0 &&
                      selectedFileInfos.length < fileInfos.length
                    }
                    checked={
                      selectedFileInfos.length > 0 &&
                      selectedFileInfos.length <= fileInfos.length
                    }
                    onChange={handleCheckboxChange}
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.fileNameTableCell}`}
                  key="table-header-col-fileName"
                  sortDirection={
                    sortState.key === "fileName" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "fileName"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("fileName")}
                  >
                    フォルダ名／ファイル名
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.fileLastModifiedTableCell}`}
                  key="table-header-col-lastModified"
                  sortDirection={
                    sortState.key === "lastModified" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "lastModified"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("lastModified")}
                  >
                    最終更新日時
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.fileSizeTableCell}`}
                  key="table-header-col-fileSize"
                  sortDirection={
                    sortState.key === "fileSize" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "fileSize"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("fileSize")}
                  >
                    サイズ
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <ProgressView />
                  </TableCell>
                </TableRow>
              ) : (
                <FilesTableRow />
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {/* ダウンロード確認ダイアログ */}
      <FileActionDialog
        title="ダウンロード"
        message={
          <div style={{ whiteSpace: "pre-line" }}>{downloadMessage}</div>
        }
        buttonLabel="DOWNLOAD"
        isOpenDialog={isOpenDownloadDialog}
        onAction={onActionDownloadFile}
        onClose={() => setIsOpenDownloadDialog(false)}
        targetFileInfos={selectedFileInfos}
      />
      {/* 削除確認ダイアログ */}
      <FileActionDialog
        title="ファイル削除"
        message="選択済みのファイルを削除しますか？"
        buttonLabel="DELETE"
        isOpenDialog={isOpenDeleteDialog}
        onAction={() =>
          dispatch(fetchDeleteFile(filePrefix, selectedFileInfos))
        }
        onClose={() => setIsOpenDeleteDialog(false)}
        targetFileInfos={selectedFileInfos}
      />
      {/* アップロードダイアログ */}
      <UploadDialog
        isOpenDialog={isOpenUploadDialog}
        onClose={() => setIsOpenUploadDialog(false)}
        filePrefix={filePrefix}
      />
      {/* フォルダ作成ダイアログ */}
      <CreateFolderDialog
        isOpenDialog={isOpenCreateFolderDialog}
        onClose={() => setIsOpenCreateFolderDialog(false)}
        filePrefix={filePrefix}
      />
    </>
  );
}

export default FilesCard;
